import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { getMethod, postMethod, patchMethod, putMethod, deleteMethod } from "../../services/ApiCallMethods";

// ── Thunks ─────────────────────────────────────────────────────────────────

export const fetchRelatedParties = createAsyncThunk(
  "relatedParty/fetchList",
  async ({ clientId, search = "", page = 1, limit = 10 }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams({ search, page, limit });
      const res = await getMethod(`/api/clients/${clientId}/related-parties?${params}`);
      const data = await res.json();
      if (!res.ok) return rejectWithValue(data);
      return data;
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to fetch related parties");
    }
  }
);

export const fetchAllRelatedParties = createAsyncThunk(
  "relatedParty/fetchAllList",
  async ({ clientId }, { rejectWithValue }) => {
    try {
      const res = await getMethod(`/api/clients/${clientId}/related-parties/all`);
      const data = await res.json();
      if (!res.ok) return rejectWithValue(data);
      return data.data || [];
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to fetch related parties");
    }
  }
);

export const fetchRelationshipTypes = createAsyncThunk(
  "relatedParty/fetchRelationshipTypes",
  async (_, { rejectWithValue }) => {
    try {
      const res = await getMethod(`/api/related-parties/relationship-types`);
      const data = await res.json();
      if (!res.ok) return rejectWithValue(data);
      return data.data || [];
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to fetch relationship types");
    }
  }
);

export const fetchShareholderAccounts = createAsyncThunk(
  "relatedParty/fetchShareholderAccounts",
  async ({ clientId }, { rejectWithValue }) => {
    try {
      const res = await getMethod(`/api/clients/${clientId}/shareholder-accounts`);
      const data = await res.json();
      if (!res.ok) return rejectWithValue(data);
      return data.data || [];
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to fetch shareholder accounts");
    }
  }
);

export const fetchShareholderAccountsWithParties = createAsyncThunk(
  "relatedParty/fetchShareholderAccountsWithParties",
  async ({ clientId, search = "" }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams({ search });
      const res = await getMethod(`/api/clients/${clientId}/shareholder-accounts-with-parties?${params}`);
      const data = await res.json();
      if (!res.ok) return rejectWithValue(data);
      return data.data || [];
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to fetch accounts with parties");
    }
  }
);

export const checkEmailExists = createAsyncThunk(
  "relatedParty/checkEmail",
  async ({ email, clientId }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams({ email });
      if (clientId) params.set("clientId", clientId);
      const res = await getMethod(`/api/related-parties/check-email?${params}`);
      const data = await res.json();
      if (!res.ok) return rejectWithValue(data);
      return data; // { exists: bool, is_already_rp: bool, users: [] }
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to check email");
    }
  }
);

export const addRelatedParty = createAsyncThunk(
  "relatedParty/add",
  async ({ clientId, payload }, { rejectWithValue }) => {
    try {
      const res = await postMethod(`/api/clients/${clientId}/related-parties`, payload);
      const data = await res.json();
      if (!res.ok) return rejectWithValue(data);
      return data;
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to add related party");
    }
  }
);

export const fetchRpAccountAccess = createAsyncThunk(
  "relatedParty/fetchRpAccess",
  async ({ clientId, rpId }, { rejectWithValue }) => {
    try {
      const res = await getMethod(`/api/clients/${clientId}/related-parties/${rpId}/access`);
      const data = await res.json();
      if (!res.ok) return rejectWithValue(data);
      return data.data || [];
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to fetch RP access");
    }
  }
);

export const updateAccountAccess = createAsyncThunk(
  "relatedParty/updateAccess",
  async ({ clientId, rpId, owner_entity_ids }, { rejectWithValue }) => {
    try {
      const res = await patchMethod(`/api/clients/${clientId}/related-parties/${rpId}/access`, { owner_entity_ids });
      const data = await res.json();
      if (!res.ok) return rejectWithValue(data);
      return data;
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to update account access");
    }
  }
);

export const adminBulkGrantAccess = createAsyncThunk(
  "relatedParty/bulkGrant",
  async ({ clientId, related_party_ids, owner_entity_ids }, { rejectWithValue }) => {
    try {
      const res = await postMethod(`/api/clients/${clientId}/related-parties/manage-access`, { related_party_ids, owner_entity_ids });
      const data = await res.json();
      if (!res.ok) return rejectWithValue(data);
      return data;
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to grant access");
    }
  }
);

export const approveRpRequest = createAsyncThunk(
  "relatedParty/approveRequest",
  async ({ requestId, user_id, owner_entity_ids }, { rejectWithValue }) => {
    try {
      const body = {};
      if (user_id) body.user_id = user_id;
      if (owner_entity_ids?.length) body.owner_entity_ids = owner_entity_ids;
      const res = await postMethod(`/api/change-requests/${requestId}/approve-rp`, body);
      const data = await res.json();
      if (!res.ok) return rejectWithValue(data);
      return data;
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to approve request");
    }
  }
);

export const resendRpInvite = createAsyncThunk(
  "relatedParty/resendInvite",
  async ({ clientId, mappingId }, { rejectWithValue }) => {
    try {
      const res = await postMethod(`/api/clients/${clientId}/related-parties/${mappingId}/resend-invite`, {});
      const data = await res.json();
      if (!res.ok) return rejectWithValue(data);
      return data;
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to resend invite");
    }
  }
);

export const deleteRelatedParty = createAsyncThunk(
  "relatedParty/delete",
  async ({ clientId, mappingId }, { rejectWithValue }) => {
    try {
      const res = await deleteMethod(`/api/clients/${clientId}/related-parties/${mappingId}`);
      const data = await res.json();
      if (!res.ok) return rejectWithValue(data);
      return data;
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to delete related party");
    }
  }
);

export const updateRpStatus = createAsyncThunk(
  "relatedParty/updateStatus",
  async ({ clientId, mappingId, rp_status }, { rejectWithValue }) => {
    try {
      const res = await patchMethod(`/api/clients/${clientId}/related-parties/${mappingId}/status`, { rp_status });
      const data = await res.json();
      if (!res.ok) return rejectWithValue(data);
      return data;
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to update status");
    }
  }
);

export const submitRpChangeRequest = createAsyncThunk(
  "relatedParty/submitChangeRequest",
  async ({ requester_id, request_type, rp_details, additional_comments }, { rejectWithValue }) => {
    try {
      const res = await postMethod(`/api/change-requests`, {
        requester_id,
        request_type,
        rp_details,
        others: additional_comments || null,
      });
      const data = await res.json();
      if (!res.ok) return rejectWithValue(data);
      return data;
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to submit request");
    }
  }
);

// ── Slice ───────────────────────────────────────────────────────────────────

const relatedPartySlice = createSlice({
  name: "relatedParty",
  initialState: {
    parties: [],
    allParties: [],
    pagination: null,
    relationshipTypes: [],
    shareholderAccounts: [],
    shareholderAccountsWithParties: [],
    rpAccess: [],
    selectedPartyName: null,
    emailCheck: null,
    loading: false,
    emailCheckLoading: false,
    actionLoading: false,
    error: null,
    actionError: null,
    successMessage: null,
  },
  reducers: {
    clearEmailCheck(state) { state.emailCheck = null; },
    clearError(state) { state.error = null; state.actionError = null; },
    clearSuccess(state) { state.successMessage = null; },
    setSelectedPartyName(state, action) { state.selectedPartyName = action.payload; },
  },
  extraReducers: (builder) => {
    // fetchRelatedParties
    builder
      .addCase(fetchRelatedParties.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchRelatedParties.fulfilled, (state, action) => {
        state.loading = false;
        state.parties = action.payload.data || [];
        state.pagination = action.payload.pagination || null;
      })
      .addCase(fetchRelatedParties.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to load";
      });

    // fetchAllRelatedParties
    builder
      .addCase(fetchAllRelatedParties.fulfilled, (state, action) => {
        state.allParties = action.payload;
      });

    // fetchRelationshipTypes
    builder
      .addCase(fetchRelationshipTypes.fulfilled, (state, action) => {
        state.relationshipTypes = action.payload;
      });

    // fetchShareholderAccounts
    builder
      .addCase(fetchShareholderAccounts.fulfilled, (state, action) => {
        state.shareholderAccounts = action.payload;
      });

    // fetchShareholderAccountsWithParties
    builder
      .addCase(fetchShareholderAccountsWithParties.pending, (state) => { state.loading = true; })
      .addCase(fetchShareholderAccountsWithParties.fulfilled, (state, action) => {
        state.loading = false;
        state.shareholderAccountsWithParties = action.payload;
      })
      .addCase(fetchShareholderAccountsWithParties.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to load";
      });

    // checkEmailExists
    builder
      .addCase(checkEmailExists.pending, (state) => { state.emailCheckLoading = true; state.emailCheck = null; })
      .addCase(checkEmailExists.fulfilled, (state, action) => {
        state.emailCheckLoading = false;
        state.emailCheck = action.payload;
      })
      .addCase(checkEmailExists.rejected, (state) => { state.emailCheckLoading = false; });

    // addRelatedParty
    builder
      .addCase(addRelatedParty.pending, (state) => { state.actionLoading = true; state.actionError = null; })
      .addCase(addRelatedParty.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.successMessage = action.payload.message;
      })
      .addCase(addRelatedParty.rejected, (state, action) => {
        state.actionLoading = false;
        state.actionError = action.payload?.message || "Failed";
      });

    // fetchRpAccountAccess
    builder
      .addCase(fetchRpAccountAccess.fulfilled, (state, action) => {
        state.rpAccess = action.payload;
      });

    // updateAccountAccess
    builder
      .addCase(updateAccountAccess.pending, (state) => { state.actionLoading = true; state.actionError = null; })
      .addCase(updateAccountAccess.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.successMessage = action.payload.message;
      })
      .addCase(updateAccountAccess.rejected, (state, action) => {
        state.actionLoading = false;
        state.actionError = action.payload?.message || "Failed";
      });

    // adminBulkGrantAccess
    builder
      .addCase(adminBulkGrantAccess.pending, (state) => { state.actionLoading = true; state.actionError = null; })
      .addCase(adminBulkGrantAccess.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.successMessage = action.payload.message;
      })
      .addCase(adminBulkGrantAccess.rejected, (state, action) => {
        state.actionLoading = false;
        state.actionError = action.payload?.message || "Failed";
      });

    // approveRpRequest
    builder
      .addCase(approveRpRequest.pending, (state) => { state.actionLoading = true; state.actionError = null; })
      .addCase(approveRpRequest.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.successMessage = action.payload.message;
      })
      .addCase(approveRpRequest.rejected, (state, action) => {
        state.actionLoading = false;
        state.actionError = action.payload?.message || "Failed";
      });

    // submitRpChangeRequest
    builder
      .addCase(submitRpChangeRequest.pending, (state) => { state.actionLoading = true; state.actionError = null; })
      .addCase(submitRpChangeRequest.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.successMessage = action.payload.message;
      })
      .addCase(submitRpChangeRequest.rejected, (state, action) => {
        state.actionLoading = false;
        state.actionError = action.payload?.message || "Failed";
      });
  },
});

export const { clearEmailCheck, clearError, clearSuccess, setSelectedPartyName } = relatedPartySlice.actions;
export default relatedPartySlice.reducer;
